"use client"

import { motion } from "framer-motion"
import { useState } from "react"
import { Globe, Smartphone, Settings, ArrowUpRight } from "lucide-react"
import { Drawer } from "vaul"
import { serviceItems } from "@/features/home/data"
import { useHaptic } from "@/hooks/use-haptic"
import { staggerContainer, staggerItem, fadeUp, sectionLabel } from "@/lib/motion"

const iconMap = { Globe, Smartphone, Settings }

const VP = { once: true, amount: 0.05 }

function ServiceCard({ service }: { service: typeof serviceItems[0] }) {
  const [isHovered, setIsHovered] = useState(false)
  const Icon = iconMap[service.iconName as keyof typeof iconMap]
  const haptic = useHaptic()

  return (
    <Drawer.Root>
      <Drawer.Trigger asChild onClick={() => haptic("light")}>
        <motion.div
          variants={staggerItem}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`relative group tilt-hover transition-all active:scale-[0.98] min-w-[85vw] snap-center sm:min-w-0 cursor-pointer text-left ${isHovered ? "z-10" : "z-0"}`}
        >
      <div
        className={`h-full glass hairline rounded-3xl p-6 sm:p-8 transition-shadow duration-300 ${isHovered ? "glow-accent" : ""}`}
      >
        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
          {Icon ? <Icon className="w-7 h-7 text-primary" /> : null}
        </div>

        <h3 className="font-[family-name:var(--font-heading)] text-xl sm:text-2xl font-bold text-foreground mb-4 leading-tight">
          {service.title}
        </h3>

        <p className="text-muted-foreground leading-snug sm:leading-relaxed mb-6">
          {service.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          {service.tech.map((tech) => (
            <span
              key={tech}
              className="px-3 py-1 text-xs font-[family-name:var(--font-mono)] bg-secondary rounded-full text-muted-foreground"
            >
              {tech}
            </span>
          ))}
        </div>

        <div className="flex items-center text-primary font-medium group-hover:gap-2 transition-all">
          <span>Detaylı Bilgi</span>
          <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </div>
      </div>
      </motion.div>
      </Drawer.Trigger>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50" />
        <Drawer.Content className="bg-background flex flex-col rounded-t-[2rem] h-[85vh] sm:h-auto sm:max-h-[85vh] fixed bottom-0 left-0 right-0 z-50 border border-border/50 shadow-2xl focus:outline-none">
           <div className="p-6 flex-1 overflow-y-auto hide-scrollbar flex flex-col">
              <div className="mx-auto w-12 h-1.5 rounded-full bg-border mb-8 shrink-0" />
              
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 shrink-0">
                {Icon ? <Icon className="w-8 h-8 text-primary" /> : null}
              </div>

              <Drawer.Title className="font-[family-name:var(--font-heading)] text-3xl font-bold mb-4">{service.title}</Drawer.Title>
              <p className="text-muted-foreground leading-relaxed mb-8 text-lg">{service.description}</p>
              
              <h4 className="font-semibold mb-4 text-foreground">Kullanılan Teknolojiler</h4>
              <div className="flex flex-wrap gap-2 mb-8">
                 {service.tech.map((tech) => (
                   <span key={tech} className="px-4 py-2 bg-secondary rounded-xl text-sm font-medium text-foreground">
                     {tech}
                   </span>
                 ))}
              </div>

              <div className="mt-auto pt-6 border-t border-border/50">
                <Drawer.Close asChild>
                  <button onClick={() => haptic("medium")} className="w-full bg-primary text-primary-foreground py-4 rounded-xl font-semibold active:scale-[0.98] transition-transform">
                    Anladım, Kapat
                  </button>
                </Drawer.Close>
              </div>
           </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

export function ServicesSection() {
  return (
    <section id="hizmetler" className="relative py-16 sm:py-24 lg:py-32" aria-label="Hizmetlerimiz">
      <div className="brand-divider" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.span variants={sectionLabel} className="text-primary font-medium text-sm uppercase tracking-wider inline-block">
            Neler Yapıyoruz?
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-5 sm:mb-6 text-foreground leading-tight">
            Uzmanlık <span className="text-gradient-brand">Alanlarımız</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-base sm:text-lg leading-snug sm:leading-relaxed">
            En güncel teknolojiler ve en iyi pratiklerle, her ölçekte projeye
            mükemmel çözümler sunuyoruz.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6"
        >
          {serviceItems.map((service) => (
            <ServiceCard key={service.title} service={service} />
          ))}
        </motion.div>

      </div>
    </section>
  )
}
